import { z } from 'zod';
import { carrierPostalCodeMatchKindSchema, carrierResolutionSchema, carrierTextMatchKindSchema, } from '../status/carrier.js';
import { isoDateTimeSchema, objectIdSchema } from './common.js';
/** Code pays ISO-3166-1 alpha-2, en majuscules. */
export const countryCodeSchema = z
    .string()
    .length(2)
    .regex(/^[A-Z]{2}$/, 'code pays ISO-2 attendu');
/** Comparaison textuelle du mode de livraison (`shipping_lines[0]`). */
export const carrierTextConditionSchema = z.object({
    match: carrierTextMatchKindSchema,
    value: z.string().min(1),
});
export const carrierPostalCodeConditionSchema = z.object({
    match: carrierPostalCodeMatchKindSchema,
    value: z.string().min(1),
});
/**
 * Conditions d'une règle (`01` §2.3). Chaque dimension omise (liste vide,
 * `null`) ne contraint rien ; toutes celles renseignées doivent être
 * satisfaites — c'est un ET.
 */
export const carrierRuleConditionsSchema = z.object({
    /** Codes boutique et/ou `pennylane`. Vide = toutes les origines. */
    origins: z.array(z.string().min(1)),
    /** Mode de livraison Shopify ; `null` = pas de contrainte. */
    shippingMethod: carrierTextConditionSchema.nullable(),
    /** Pays destinataire ; vide = tous. */
    countries: z.array(countryCodeSchema),
    postalCode: carrierPostalCodeConditionSchema.nullable(),
    /** Canaux (`source_name`) ; vide = tous. */
    channels: z.array(z.string().min(1)),
});
/** Ce que la règle impose quand elle est retenue. */
export const carrierRuleResultSchema = z.object({
    /** Code transporteur Martelet (`IC1` col. 55). */
    code: z.string().min(1),
    /** Libellé transporteur (`IC1` col. 56). */
    label: z.string().min(1),
    /** La commande part en point relais : le code relais devient obligatoire. */
    pickup: z.boolean(),
    /** Pays destinataire forcé (`MC` → `FR` de la v1) ; `null` = inchangé. */
    countryOverride: countryCodeSchema.nullable(),
});
/** Règle transporteur — globale et ordonnée, premier match gagnant (`03` §2, `01` §2.3). */
export const carrierRuleSchema = z.object({
    id: objectIdSchema,
    /** Rang dans la liste ; le plus petit est évalué en premier. */
    order: z.number().int().min(0),
    /** Libellé lisible, affiché dans la liste et le simulateur. */
    name: z.string().min(1),
    conditions: carrierRuleConditionsSchema,
    result: carrierRuleResultSchema,
    lastMatchedAt: isoDateTimeSchema.nullable(),
    updatedAt: isoDateTimeSchema,
    updatedBy: objectIdSchema.nullable(),
});
/** `order` est attribué par le serveur (fin de liste à la création). */
export const carrierRuleInputSchema = carrierRuleSchema.omit({
    id: true,
    order: true,
    lastMatchedAt: true,
    updatedAt: true,
    updatedBy: true,
});
/**
 * `POST /api/carriers/rules/reorder` — l'**ensemble complet** des règles, dans
 * l'ordre voulu. Une liste partielle est rejetée : réordonner sur une vue
 * incomplète produirait un ordre incohérent.
 */
export const carrierRuleReorderBodySchema = z.object({
    ruleIds: z.array(objectIdSchema).min(1),
});
/** Réponse `{items}` de la liste de règles (`02` §9), non paginée et ordonnée. */
export const carrierRuleListResponseSchema = z.object({
    items: z.array(carrierRuleSchema),
});
/** Entrée du simulateur (`02` §9). */
export const carrierSimulationBodySchema = z.object({
    /** Code boutique ou `pennylane`. */
    origin: z.string().min(1),
    /** Mode de livraison saisi ; `null` = commande sans ligne de livraison. */
    shippingMethod: z.string().nullable(),
    country: countryCodeSchema,
    zip: z.string().optional(),
    channel: z.string().default('web'),
});
export const carrierSimulationResultSchema = z.object({
    ruleId: objectIdSchema.nullable(),
    ruleName: z.string().nullable(),
    resolution: carrierResolutionSchema,
    code: z.string(),
    label: z.string(),
    pickup: z.boolean(),
    countryCodeApplied: countryCodeSchema,
    usedDefaultCarrier: z.boolean(),
    /** Une étape de raisonnement par ligne, en français. */
    steps: z.array(z.string()),
    /** Les étapes concaténées, prêtes à afficher sous le formulaire. */
    explanation: z.string(),
});
/** Une cellule de la matrice de couverture : un mode de livraison × un pays. */
export const carrierCoverageCellSchema = z.object({
    country: countryCodeSchema,
    ruleId: objectIdSchema.nullable(),
    ruleName: z.string().nullable(),
    code: z.string(),
    label: z.string(),
    /** `true` quand aucune règle ne couvre ce couple : trou de configuration. */
    usedDefaultCarrier: z.boolean(),
});
export const carrierCoverageRowSchema = z.object({
    profileId: z.string(),
    profileName: z.string(),
    zoneName: z.string(),
    shippingMethod: z.string(),
    /** Le tarif est-il actif chez Shopify ? */
    active: z.boolean(),
    /** Zone « Reste du monde » : la liste de pays y est indicative. */
    restOfWorld: z.boolean(),
    cells: z.array(carrierCoverageCellSchema),
});
export const carrierCoverageQuerySchema = z.object({
    store: z.string().min(1),
});
export const carrierCoverageResponseSchema = z.object({
    store: z.string(),
    fetchedAt: isoDateTimeSchema,
    /** Tous les modes de livraison rencontrés, dédoublonnés — alimente le simulateur. */
    shippingMethods: z.array(z.string()),
    rows: z.array(carrierCoverageRowSchema),
});
/** Transporteur par défaut d'une origine — en lecture seule ici (`02` §9). */
export const carrierDefaultSchema = z.object({
    origin: z.string(),
    name: z.string(),
    /** `null` pour Pennylane quand aucun transporteur n'est configuré : bloc transport vide (v1). */
    defaultCarrier: z.object({ code: z.string(), label: z.string() }).nullable(),
});
export const carrierDefaultsResponseSchema = z.object({
    items: z.array(carrierDefaultSchema),
});
//# sourceMappingURL=carriers.js.map